/**
 * TravelReservationsPanel — discovery hub for bookable experiences.
 *
 * Opens as a drawer (like Budget / Packing) with category tabs — Restaurants,
 * Attractions, Excursions, Events. "Find reservations" runs a web-search
 * discovery that surfaces things to book in advance, going beyond the obvious
 * and beyond what's already on the itinerary (incl. off-the-route excursions a
 * town over). Each suggestion can be added to the itinerary, marked booked, or
 * dismissed.
 */

import { useMemo, useState } from 'react';
import {
  Utensils,
  Ticket,
  Compass,
  CalendarDays,
  Loader2,
  ExternalLink,
  Plus,
  Check,
  X,
  Sparkles,
  ScanSearch,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import { useReservationSuggestions } from '../hooks';
import type {
  TravelReservationSuggestionResponse,
  TravelReservationSuggestionCategory,
} from '../api';
import { sanitizeSourceUrl } from './TravelHelpers';

const CATEGORIES: Array<{
  key: TravelReservationSuggestionCategory;
  label: string;
  icon: typeof Utensils;
}> = [
  { key: 'restaurant', label: 'Restaurants', icon: Utensils },
  { key: 'attraction', label: 'Attractions', icon: Ticket },
  { key: 'excursion', label: 'Excursions', icon: Compass },
  { key: 'event', label: 'Events', icon: CalendarDays },
];

export function TravelReservationsPanel({
  tripId,
  open,
  onOpenChange,
  onAddToItinerary,
  canAddToItinerary,
}: {
  tripId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Adds the suggestion to the itinerary (creates an event). */
  onAddToItinerary: (s: TravelReservationSuggestionResponse) => Promise<void>;
  canAddToItinerary: boolean;
}) {
  const { suggestions, loading, discover, isDiscovering, setStatus, remove } =
    useReservationSuggestions(tripId);
  const [activeTab, setActiveTab] = useState<TravelReservationSuggestionCategory>('restaurant');

  // Hide dismissed; group by category.
  const byCategory = useMemo(() => {
    const map: Record<TravelReservationSuggestionCategory, TravelReservationSuggestionResponse[]> =
      {
        restaurant: [],
        attraction: [],
        excursion: [],
        event: [],
      };
    for (const s of suggestions) {
      if (s.status === 'dismissed') continue;
      map[s.category]?.push(s);
    }
    return map;
  }, [suggestions]);

  const total = suggestions.filter((s) => s.status !== 'dismissed').length;

  const handleDiscover = async () => {
    try {
      const res = await discover();
      toast({
        title: res.addedCount > 0 ? `Found ${res.addedCount} new` : 'No new suggestions',
        description: res.message,
      });
    } catch (err) {
      toast({
        title: 'Discovery failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const handleAdd = async (s: TravelReservationSuggestionResponse) => {
    if (!canAddToItinerary) {
      toast({
        title: 'Add a day first',
        description: 'Create a day on the itinerary to add to it.',
      });
      return;
    }
    try {
      await onAddToItinerary(s);
      await setStatus(s.suggestionId, 'interested');
      toast({ title: 'Added to itinerary', description: s.title });
    } catch (err) {
      toast({
        title: 'Could not add to itinerary',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ScanSearch className="h-5 w-5" /> Reservations
          </SheetTitle>
          <SheetDescription>
            Bookable experiences worth reserving ahead — found via live web search, beyond the
            obvious. Add ones you like to your itinerary.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-3">
          <Button
            onClick={() => void handleDiscover()}
            disabled={isDiscovering}
            className="w-full"
            variant="secondary"
          >
            {isDiscovering ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Searching…
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                {total > 0 ? 'Find more reservations' : 'Find reservations'}
              </>
            )}
          </Button>

          {loading && total === 0 ? (
            <div className="space-y-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : (
            <Tabs
              value={activeTab}
              onValueChange={(v) => setActiveTab(v as TravelReservationSuggestionCategory)}
            >
              <TabsList className="grid w-full grid-cols-4">
                {CATEGORIES.map((c) => {
                  const Icon = c.icon;
                  const count = byCategory[c.key].length;
                  return (
                    <TabsTrigger key={c.key} value={c.key} className="text-xs px-1">
                      <Icon className="h-3.5 w-3.5 mr-1 shrink-0" />
                      <span className="hidden sm:inline">{c.label}</span>
                      {count > 0 && <span className="ml-1 text-muted-foreground">{count}</span>}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {CATEGORIES.map((c) => (
                <TabsContent key={c.key} value={c.key} className="mt-3">
                  {byCategory[c.key].length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      No {c.label.toLowerCase()} yet. Hit “{total > 0 ? 'Find more' : 'Find'}{' '}
                      reservations” to discover some.
                    </div>
                  ) : (
                    <ul className="space-y-2">
                      {byCategory[c.key].map((s) => (
                        <SuggestionCard
                          key={s.suggestionId}
                          s={s}
                          onAdd={() => void handleAdd(s)}
                          onBooked={() =>
                            void setStatus(
                              s.suggestionId,
                              s.status === 'booked' ? 'suggested' : 'booked',
                            )
                          }
                          onDismiss={() => void remove(s.suggestionId)}
                        />
                      ))}
                    </ul>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function SuggestionCard({
  s,
  onAdd,
  onBooked,
  onDismiss,
}: {
  s: TravelReservationSuggestionResponse;
  onAdd: () => void;
  onBooked: () => void;
  onDismiss: () => void;
}) {
  const bookingUrl = sanitizeSourceUrl(s.url);
  const meta = [
    s.location,
    s.duration,
    s.leadTimeDays != null ? `book ~${s.leadTimeDays}d ahead` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <li className="rounded border border-border/60 p-2.5 text-sm">
      <div className="flex items-start justify-between gap-2">
        <span className="font-medium min-w-0">{s.title}</span>
        {s.status === 'booked' && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full shrink-0 inline-flex items-center gap-1 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">
            <Check className="h-2.5 w-2.5" /> Booked
          </span>
        )}
        {s.status === 'interested' && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full shrink-0 bg-sky-500/15 text-sky-700 dark:text-sky-400">
            On itinerary
          </span>
        )}
      </div>
      {meta && <div className="mt-0.5 text-xs text-muted-foreground">{meta}</div>}
      {s.description && <div className="mt-1 text-xs text-muted-foreground">{s.description}</div>}
      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
        {bookingUrl && (
          <a
            href={bookingUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary underline mr-1"
          >
            Book{s.bookingMethod ? ` (${s.bookingMethod})` : ''}{' '}
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={onAdd}>
          <Plus className="h-3 w-3 mr-1" /> Itinerary
        </Button>
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={onBooked}>
          <Check className="h-3 w-3 mr-1" /> {s.status === 'booked' ? 'Unbook' : 'Booked'}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
          onClick={onDismiss}
          aria-label="Dismiss suggestion"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    </li>
  );
}
